import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../config/region';
import DashboardNavBar from './DashboardNavBar';
import { buildVietQrPayload, renderBrandedQr } from '../../utils/vietQrBuilder';
import { dtLocale } from '../../utils/dateLocale';
import PayosLivePanel from './PayosLivePanel';   // EV-1218 — live landed-payments + waiter QR

// ─── helpers ────────────────────────────────────────────────────────────────

function getAuthToken() {
  return (
    localStorage.getItem('venueapp_token') ||
    localStorage.getItem('venue_app_token') ||
    localStorage.getItem('token') ||
    null
  );
}

function Spinner({ size = 40, color = '#3b82f6' }) {
  return (
    <>
      <div style={{
        width: size, height: size,
        border: `4px solid rgba(0,0,0,0.08)`,
        borderTop: `4px solid ${color}`,
        borderRadius: '50%',
        animation: 'hl-spin 0.9s linear infinite',
        margin: '0 auto',
      }} />
      <style>{`@keyframes hl-spin{0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}</style>
    </>
  );
}

function ButtonSpinner() {
  return (
    <span style={{
      display: 'inline-block', width: 16, height: 16,
      border: '2px solid rgba(255,255,255,0.4)',
      borderTop: '2px solid white',
      borderRadius: '50%',
      animation: 'hl-spin 0.9s linear infinite',
      verticalAlign: 'middle', marginRight: 8,
    }} />
  );
}

function Toast({ message, visible }) {
  return (
    <div style={{
      position: 'fixed', left: '50%', top: 80, transform: `translateX(-50%) translateY(${visible ? 0 : -20}px)`,
      opacity: visible ? 1 : 0, transition: 'all 0.2s ease',
      background: '#1f2937', color: 'white', fontSize: 14, fontWeight: 600,
      padding: '10px 22px', borderRadius: 24,
      boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
      zIndex: 9999, whiteSpace: 'nowrap', pointerEvents: 'none',
    }}>
      {message}
    </div>
  );
}

// ─── EV-1241: Payment history (payer/date/amount/ITEMS/notes, sortable) ───────
// Fetches the venue's payment log from the dashboard payments endpoint (same one
// the APK consumes) and renders a compact, mobile-friendly sortable list. Tapping
// a row expands to reveal its line items. Backend: getVenueDashboardPayments.
function PaymentHistoryPanel({ slug }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [state, setState] = useState('loading'); // loading | ready | error
  const [sort, setSort] = useState('confirmed_at'); // confirmed_at | amount_vnd
  const [dir, setDir] = useState('desc');           // asc | desc
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    const token = getAuthToken();
    if (!token) { setState('error'); return; }
    setState('loading');
    try {
      const base = getApiBaseUrl();
      const qs = new URLSearchParams({ sort, dir, limit: '100' }).toString();
      const res = await fetch(`${base}/venue-dashboard/${encodeURIComponent(slug)}/payments?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setState('error'); return; }
      const data = await res.json().catch(() => ({}));
      if (!data.success) { setState('error'); return; }
      setRows(Array.isArray(data.payments) ? data.payments : []);
      setState('ready');
    } catch (err) {
      console.error('[PaymentHistory] fetch error:', err);
      setState('error');
    }
  }, [slug, sort, dir]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (key) => {
    if (sort === key) {
      setDir(d => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSort(key);
      setDir('desc');
    }
  };

  const fmtVnd = (v) => `₫${Math.round(Number(v) || 0).toLocaleString(dtLocale())}`;
  const fmtDateTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        day: '2-digit', month: 'short',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return '—'; }
  };
  const sortArrow = (key) => (sort === key ? (dir === 'desc' ? ' ↓' : ' ↑') : '');

  return (
    <div style={{ ...styles.card, marginBottom: 12, borderTop: '4px solid #10b981' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 22 }}>📊</span>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', flex: 1 }}>{t('vd.payHistoryTitle')}</div>
        <button
          style={styles.histRefreshBtn}
          onClick={load}
          disabled={state === 'loading'}
          aria-label={t('vd.payRefreshHistory')}
        >
          {state === 'loading' ? '…' : '↻'}
        </button>
      </div>
      <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, marginBottom: 12 }}>
        {t('vd.payHistoryHint')}
      </div>

      {/* Sort controls */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button
          style={{ ...styles.histSortBtn, ...(sort === 'confirmed_at' ? styles.histSortBtnActive : {}) }}
          onClick={() => toggleSort('confirmed_at')}
        >
          {t('vd.payDate')}{sortArrow('confirmed_at')}
        </button>
        <button
          style={{ ...styles.histSortBtn, ...(sort === 'amount_vnd' ? styles.histSortBtnActive : {}) }}
          onClick={() => toggleSort('amount_vnd')}
        >
          {t('vd.payAmount')}{sortArrow('amount_vnd')}
        </button>
      </div>

      {state === 'loading' && (
        <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af', fontSize: 13 }}>{t('vd.loading')}</div>
      )}
      {state === 'error' && (
        <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af', fontSize: 13 }}>
          {t('vd.payCouldNotLoadHistory')}{' '}
          <button style={{ ...styles.histSortBtn, marginTop: 8 }} onClick={load}>{t('vd.retry')}</button>
        </div>
      )}
      {state === 'ready' && rows.length === 0 && (
        <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af', fontSize: 13 }}>
          {t('vd.payNoPaymentsYet')}
        </div>
      )}

      {state === 'ready' && rows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((r) => {
            const id = r.id || r.order_ref;
            const isConfirmed = !!r.confirmed || String(r.status || '').toLowerCase() === 'paid';
            const viaWebhook = isConfirmed &&
              /webhook|sepay|payos/i.test(String(r.confirmation_source || ''));
            const items = Array.isArray(r.items) ? r.items : [];
            const isOpen = expandedId === id;
            return (
              <div key={id} style={styles.histRow}>
                <div
                  style={styles.histRowHead}
                  onClick={() => setExpandedId(isOpen ? null : id)}
                  role="button"
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: 16, fontWeight: 800, fontFamily: "'SF Mono', monospace",
                        color: (isConfirmed && viaWebhook) ? '#059669' : '#111827',
                      }}>
                        {fmtVnd(r.amount_vnd)}
                      </span>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{fmtDateTime(r.confirmed_at || r.created_at)}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
                        {r.customer_name || '—'}
                      </span>
                      {r.table_id && (
                        <span style={styles.histTag}>{t('vd.payTableN', { id: r.table_id })}</span>
                      )}
                      {r.provider && (
                        <span style={styles.histProviderTag}>{String(r.provider).toUpperCase()}</span>
                      )}
                      <span style={{
                        ...styles.histStatusTag,
                        color: isConfirmed ? '#166534' : '#92400e',
                        background: isConfirmed ? '#dcfce7' : '#fef3c7',
                      }}>
                        {isConfirmed ? t('vd.payPaidUpper') : (r.status || t('vd.payPending'))}
                      </span>
                    </div>
                    {r.memo && (
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3, fontStyle: 'italic' }}>
                        📝 {r.memo}
                      </div>
                    )}
                  </div>
                  {items.length > 0 && (
                    <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 8, flexShrink: 0 }}>
                      {t('vd.payItemCount', { count: items.length })} {isOpen ? '▲' : '▼'}
                    </span>
                  )}
                </div>

                {isOpen && items.length > 0 && (
                  <div style={styles.histItems}>
                    {items.map((it, i) => (
                      <div key={i} style={styles.histItemRow}>
                        <span style={{ flex: 1, color: '#374151' }}>
                          {it.item_name || t('vd.payItem')}
                          {it.qty != null && <span style={{ color: '#9ca3af' }}> ×{it.qty}</span>}
                        </span>
                        {it.unit_price_vnd != null && (
                          <span style={{ color: '#6b7280', fontFamily: "'SF Mono', monospace" }}>
                            {fmtVnd(it.unit_price_vnd)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Build VietQR dynamic preview URL
function buildVietQrPreview(bin, account, holder) {
  if (!bin || !account) return null;
  const params = new URLSearchParams();
  if (holder) params.set('accountName', holder);
  const qs = params.toString();
  return `https://img.vietqr.io/image/${bin}-${account}-compact2.png${qs ? '?' + qs : ''}`;
}

// ─── main component ─────────────────────────────────────────────────────────

const VenueDashboardPaymentsPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [pageState, setPageState] = useState('loading'); // loading | ready | error-auth | error-generic
  const [venue, setVenue] = useState(null);
  const [myRole, setMyRole] = useState(null);

  const [methods, setMethods] = useState([]);
  const [banks, setBanks] = useState([]);

  const [editingId, setEditingId] = useState(null); // 'new' or an existing method id
  const [formBankCode, setFormBankCode] = useState('');
  const [formAccountNumber, setFormAccountNumber] = useState('');
  const [formAccountHolder, setFormAccountHolder] = useState('');
  const [formIsPrimary, setFormIsPrimary] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [toastVisible, setToastVisible] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const toastTimerRef = useRef(null);

  // ─── Create Bill (ad-hoc) ───
  const [billAmount, setBillAmount] = useState('');
  const [billTable, setBillTable] = useState('');
  const [billMemo, setBillMemo] = useState('');
  const [billMethodId, setBillMethodId] = useState('');

  // ── load ──
  const fetchData = useCallback(async () => {
    setPageState('loading');
    const token = getAuthToken();
    if (!token) {
      setPageState('error-auth');
      return;
    }
    try {
      const base = getApiBaseUrl();
      // Fetch venue dashboard info (for role + venue id/name)
      const dashRes = await fetch(`${base}/venue-dashboard/${encodeURIComponent(slug)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (dashRes.status === 401) { setPageState('error-auth'); return; }
      if (!dashRes.ok) { setPageState('error-generic'); return; }
      const dashData = await dashRes.json();
      if (!dashData.success) { setPageState('error-generic'); return; }

      const v = dashData.venue || {};
      setVenue(v);
      setMyRole(dashData.myRole || null);

      // Fetch current payment methods (public endpoint, same as customer flow)
      const methodsRes = await fetch(`${base}/venues/${encodeURIComponent(slug)}/payment-methods`).catch(() => null);
      let list = [];
      if (methodsRes && methodsRes.ok) {
        const data = await methodsRes.json().catch(() => ({}));
        const raw = data.methods ?? data.data ?? data ?? [];
        list = Array.isArray(raw) ? raw : [];
      }
      setMethods(list);

      // Fetch bank list
      const banksRes = await fetch(`${base}/banks/vietqr`).catch(() => null);
      let banksList = [];
      if (banksRes && banksRes.ok) {
        const bdata = await banksRes.json().catch(() => []);
        const raw = Array.isArray(bdata) ? bdata : (bdata?.data ?? []);
        banksList = Array.isArray(raw) ? raw : [];
      }
      setBanks(banksList);

      setPageState('ready');
    } catch (err) {
      console.error('[PaymentsPage] fetch error:', err);
      setPageState('error-generic');
    }
  }, [slug]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    return () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); };
  }, []);

  const showToast = (msg) => {
    setToastMsg(msg);
    setToastVisible(true);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastVisible(false), 2200);
  };

  const canEdit = myRole === 'owner' || myRole === 'manager';

  // ── Default bill method to primary when methods load ──
  useEffect(() => {
    if (methods.length && !billMethodId) {
      const primary = methods.find(m => m.is_primary) || methods[0];
      setBillMethodId(String(primary.id));
    }
  }, [methods, billMethodId]);

  // ── Build the shareable bill URL ──
  const billAmountNum = useMemo(() => {
    const n = parseInt(String(billAmount).replace(/[^\d]/g, ''), 10);
    return Number.isFinite(n) ? n : 0;
  }, [billAmount]);

  const billUrl = useMemo(() => {
    if (!slug || billAmountNum <= 0) return '';
    const params = new URLSearchParams();
    params.set('amount', String(billAmountNum));
    if (billMemo) params.set('memo', billMemo);
    if (billMethodId) params.set('method', billMethodId);
    const tablePath = billTable ? `/${encodeURIComponent(billTable)}` : '';
    const origin = (typeof window !== 'undefined') ? window.location.origin : 'https://thai.honestlee.app';
    return `${origin}/pay/${encodeURIComponent(slug)}${tablePath}?${params.toString()}`;
  }, [slug, billAmountNum, billMemo, billMethodId, billTable]);

  const handleCopyBillUrl = () => {
    if (!billUrl) return;
    navigator.clipboard.writeText(billUrl).then(() => showToast(`📋 ${t('common.linkCopied')}`));
  };

  const handleShareWhatsApp = () => {
    if (!billUrl) return;
    const text = `${t('vd.payBillLabel')}${billTable ? ' ' + t('vd.payForTable', { table: billTable }) : ''}: ₫${billAmountNum.toLocaleString(dtLocale())}\n${billUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleOpenBill = () => {
    if (billUrl) window.open(billUrl, '_blank');
  };

  // Active form bank (for live QR preview)
  const selectedBank = useMemo(
    () => banks.find(b => (b.code || '').toUpperCase() === formBankCode.toUpperCase()) || null,
    [banks, formBankCode]
  );

  // Self-hosted preview QR (canvas) — no img.vietqr.io dependency
  const previewCanvasRef = useRef(null);
  useEffect(() => {
    if (!previewCanvasRef.current) return;
    if (!selectedBank?.bin || !formAccountNumber.trim()) return;
    let cancelled = false;
    (async () => {
      try {
        const payload = buildVietQrPayload({
          bankBin: selectedBank.bin,
          accountNumber: formAccountNumber.trim(),
          holderName: formAccountHolder.trim() || undefined,
        });
        if (cancelled || !previewCanvasRef.current) return;
        await renderBrandedQr(payload, previewCanvasRef.current, {
          bankCode: selectedBank.code || null,
          bankName: selectedBank.shortName || selectedBank.name || null,
        });
      } catch (e) { console.error('[Payments preview] render failed:', e); }
    })();
    return () => { cancelled = true; };
  }, [selectedBank, formAccountNumber, formAccountHolder]);
  const hasPreview = !!(selectedBank?.bin && formAccountNumber.trim());

  // ── form handlers ──
  const startAdd = () => {
    setEditingId('new');
    setFormBankCode('');
    setFormAccountNumber('');
    setFormAccountHolder('');
    setFormIsPrimary(methods.length === 0);
    setSaveError('');
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setFormBankCode((m.bank_code || '').toUpperCase());
    setFormAccountNumber(m.account_number || '');
    setFormAccountHolder(m.account_holder_name || '');
    setFormIsPrimary(!!m.is_primary);
    setSaveError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setSaveError('');
  };

  const handleSave = async () => {
    setSaveError('');
    if (!selectedBank?.bin) {
      setSaveError(t('vd.payPleaseSelectBank'));
      return;
    }
    if (!formAccountNumber.trim()) {
      setSaveError(t('vd.payAccountNumberRequired'));
      return;
    }
    if (!formAccountHolder.trim()) {
      setSaveError(t('vd.payAccountHolderRequired'));
      return;
    }

    const token = getAuthToken();
    if (!token) { setSaveError(t('vd.payNotLoggedIn')); return; }

    setSaving(true);
    try {
      const base = getApiBaseUrl();
      const venueId = venue?.id || venue?._id;
      const body = {
        bank_bin:   selectedBank.bin,
        bank_code:  (selectedBank.code || '').toUpperCase(),
        bank_name:  selectedBank.shortName || selectedBank.name || selectedBank.code,
        account_number: formAccountNumber.trim(),
        account_holder_name: formAccountHolder.trim(),
        is_primary: !!formIsPrimary,
      };

      let url, method;
      if (editingId === 'new') {
        url = `${base}/admin/venues/${encodeURIComponent(venueId)}/payment-methods`;
        method = 'POST';
      } else {
        url = `${base}/admin/venues/${encodeURIComponent(venueId)}/payment-methods/${encodeURIComponent(editingId)}`;
        method = 'PUT';
      }

      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (res.status === 401) { setSaveError(t('vd.sessionExpiredLogInAgain')); setSaving(false); return; }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || t('vd.serverErrorWithCode', { code: res.status }));
      }

      await fetchData();
      setEditingId(null);
      showToast(`✅ ${t('vd.saved')}`);
    } catch (err) {
      console.error('[PaymentsPage] save error:', err);
      setSaveError(err.message || t('vd.saveFailedTryAgain'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (methodId) => {
    if (!window.confirm(t('vd.payDeleteMethodConfirm'))) return;
    const token = getAuthToken();
    if (!token) return;
    try {
      const base = getApiBaseUrl();
      const venueId = venue?.id || venue?._id;
      const res = await fetch(`${base}/admin/venues/${encodeURIComponent(venueId)}/payment-methods/${encodeURIComponent(methodId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || t('vd.payDeleteFailedWithCode', { code: res.status }));
      }
      await fetchData();
      showToast(`🗑 ${t('vd.payDeleted')}`);
    } catch (err) {
      console.error('[PaymentsPage] delete error:', err);
      alert(err.message || t('vd.payDeleteFailed'));
    }
  };

  // ── render: loading ──
  if (pageState === 'loading') {
    return (
      <div style={styles.screen}>
        <style>{`@keyframes hl-spin{0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}</style>
        <Header title={t('vd.payments')} onBack={() => navigate(`/venue-dashboard/${slug}`)} />
        <div style={styles.centreBlock}>
          <Spinner />
          <p style={styles.centreText}>{t('vd.payLoadingMethods')}</p>
        </div>
      </div>
    );
  }

  if (pageState === 'error-auth') {
    return (
      <div style={styles.screen}>
        <Header title={t('vd.payments')} onBack={() => navigate(`/venue-dashboard/${slug}`)} />
        <div style={styles.centreBlock}>
          <div style={{ fontSize: 52, marginBottom: 8 }}>🔒</div>
          <p style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>{t('vd.pleaseLogIn')}</p>
          <button style={styles.primaryBtn} onClick={() => navigate('/login')}>{t('vd.logIn')}</button>
        </div>
      </div>
    );
  }

  if (pageState === 'error-generic') {
    return (
      <div style={styles.screen}>
        <Header title={t('vd.payments')} onBack={() => navigate(`/venue-dashboard/${slug}`)} />
        <div style={styles.centreBlock}>
          <div style={{ fontSize: 52, marginBottom: 8 }}>⚠️</div>
          <p style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>{t('vd.somethingWentWrong')}</p>
          <button style={styles.primaryBtn} onClick={fetchData}>{t('vd.retry')}</button>
        </div>
      </div>
    );
  }

  // ── render: ready ──
  return (
    <div style={styles.screen}>
      <style>{`@keyframes hl-spin{0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}</style>
      <Toast message={toastMsg} visible={toastVisible} />
      <Header title={t('vd.payments')} onBack={() => navigate(`/venue-dashboard/${slug}`)} />

      <div style={styles.body}>
        <div style={styles.venueNameStrip}>
          <span style={styles.venueName}>{venue?.name || t('vd.venueFallback')}</span>
        </div>

        {/* Intro card */}
        <div style={{ ...styles.card, marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 4 }}>{t('vd.payVietQrPayments')}</div>
          <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
            {t('vd.payIntroPart1')} <code style={{ background: '#f3f4f6', padding: '1px 5px', borderRadius: 4 }}>/pay/{slug}</code> {t('vd.payIntroPart2')}
          </div>
        </div>

        {/* Existing methods */}
        {methods.length === 0 && editingId !== 'new' && (
          <div style={{ ...styles.card, textAlign: 'center', padding: 30, marginBottom: 12 }}>
            <div style={{ fontSize: 44, marginBottom: 8 }}>💳</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 6 }}>{t('vd.payNoMethodsYet')}</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16, lineHeight: 1.5 }}>
              {t('vd.payAddVnBankHint')}
            </div>
            {canEdit && (
              <button style={styles.primaryBtn} onClick={startAdd}>+ {t('vd.payAddBankAccount')}</button>
            )}
          </div>
        )}

        {methods.map(m => {
          const bank = banks.find(b => b.bin === m.bank_bin) || banks.find(b => (b.code || '').toUpperCase() === (m.bank_code || '').toUpperCase());
          const logo = bank?.logo || (m.bank_code ? `https://cdn.vietqr.io/img/${m.bank_code.toUpperCase()}.png` : null);
          const isThisEditing = editingId === m.id;
          if (isThisEditing) return null; // show form below
          return (
            <div key={m.id} style={{ ...styles.card, marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {logo && <img src={logo} alt="" style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 8, background: '#f9fafb', padding: 4 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                      {bank?.shortName || m.bank_name || m.bank_code || t('vd.payBankFallback')}
                    </span>
                    {m.is_primary && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#166534', background: '#dcfce7', padding: '2px 8px', borderRadius: 8 }}>
                        {t('vd.payPrimaryUpper')}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, fontFamily: "'SF Mono', monospace", color: '#374151', marginTop: 2 }}>
                    {m.account_number}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 1 }}>
                    {m.account_holder_name}
                  </div>
                </div>
              </div>
              {canEdit && (
                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button style={styles.outlineBtnSmall} onClick={() => startEdit(m)}>{t('vd.edit')}</button>
                  <button style={{ ...styles.outlineBtnSmall, color: '#b91c1c', borderColor: '#fecaca' }} onClick={() => handleDelete(m.id)}>
                    {t('common.delete')}
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {/* Edit / add form */}
        {editingId && canEdit && (
          <div style={{ ...styles.card, marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 14 }}>
              {editingId === 'new' ? t('vd.payAddBankAccount') : t('vd.payEditBankAccount')}
            </div>

            {/* Bank dropdown */}
            <label style={{ display: 'block', marginBottom: 12 }}>
              <div style={styles.labelText}>{t('vd.payBank')}</div>
              <select
                value={formBankCode}
                onChange={(e) => setFormBankCode(e.target.value)}
                style={styles.input}
              >
                <option value="">— {t('vd.paySelectABank')} —</option>
                {banks.map(b => (
                  <option key={b.bin || b.code} value={(b.code || '').toUpperCase()}>
                    {b.shortName || b.name || b.code}
                  </option>
                ))}
              </select>
              {banks.length === 0 && (
                <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 4 }}>
                  {t('vd.payBankListNotLoaded')}
                </div>
              )}
            </label>

            {/* Account number */}
            <label style={{ display: 'block', marginBottom: 12 }}>
              <div style={styles.labelText}>{t('vd.payAccountNumber')}</div>
              <input
                type="text"
                inputMode="numeric"
                value={formAccountNumber}
                onChange={(e) => setFormAccountNumber(e.target.value.replace(/[^\d]/g, ''))}
                placeholder={t('vd.payAccountNumberPlaceholder')}
                style={{ ...styles.input, fontFamily: "'SF Mono', monospace", letterSpacing: 1 }}
              />
            </label>

            {/* Account holder */}
            <label style={{ display: 'block', marginBottom: 12 }}>
              <div style={styles.labelText}>{t('vd.payAccountHolderName')}</div>
              <input
                type="text"
                value={formAccountHolder}
                onChange={(e) => setFormAccountHolder(e.target.value.toUpperCase())}
                placeholder="NGUYEN VAN A"
                style={{ ...styles.input, textTransform: 'uppercase' }}
              />
            </label>

            {/* Primary checkbox */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formIsPrimary}
                onChange={(e) => setFormIsPrimary(e.target.checked)}
                style={{ width: 18, height: 18, accentColor: '#3b82f6' }}
              />
              <span style={{ fontSize: 13, color: '#374151' }}>
                {t('vd.paySetAsPrimaryPre')} <strong>{t('vd.payPrimary')}</strong> {t('vd.paySetAsPrimaryPost')}
              </span>
            </label>

            {/* Live QR preview — self-hosted canvas */}
            {hasPreview && (
              <div style={{ marginBottom: 14, padding: 14, background: '#f0f9ff', borderRadius: 12, border: '1px solid #bfdbfe' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                  {t('vd.payLiveQrPreview')}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <canvas ref={previewCanvasRef} style={{ width: 220, height: 'auto', borderRadius: 8, background: 'white', padding: 6 }} />
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', textAlign: 'center', marginTop: 8 }}>
                  {t('vd.payCustomersWillSeeQr')}
                </div>
              </div>
            )}

            {saveError && (
              <div style={styles.errorBanner}>{saveError}</div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                style={{ ...styles.primaryBtn, opacity: saving ? 0.75 : 1, cursor: saving ? 'default' : 'pointer' }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving && <ButtonSpinner />}
                {saving ? t('vd.saving') : (editingId === 'new' ? t('vd.payAddPaymentMethod') : t('vd.saveChanges'))}
              </button>
              <button style={styles.outlineBtn} onClick={cancelEdit} disabled={saving}>{t('vd.cancel')}</button>
            </div>
          </div>
        )}

        {/* Add another button */}
        {canEdit && methods.length > 0 && editingId !== 'new' && !methods.some(m => editingId === m.id) && (
          <button style={{ ...styles.outlineBtn, marginBottom: 12 }} onClick={startAdd}>
            + {t('vd.payAddAnotherBankAccount')}
          </button>
        )}

        {/* ────────── EV-1218: Live payments (software soundbox) + waiter-presented QR ────────── */}
        {methods.length > 0 && (
          <PayosLivePanel
            slug={slug}
            primaryMethod={methods.find((m) => m.is_primary) || methods[0]}
          />
        )}

        {/* ────────── EV-1241: Payment history (payer/date/amount/items/notes, sortable) ────────── */}
        <PaymentHistoryPanel slug={slug} />

        {/* ────────── Create Bill (ad-hoc) ────────── */}
        {methods.length > 0 && canEdit && (
          <div style={{ ...styles.card, marginBottom: 12, borderTop: '4px solid #E46E34' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 22 }}>🧾</span>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{t('vd.payCreateABill')}</div>
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, marginBottom: 14 }}>
              {t('vd.payCreateBillHint')}
            </div>

            {/* Method picker (only if multiple) */}
            {methods.length > 1 && (
              <label style={{ display: 'block', marginBottom: 10 }}>
                <div style={styles.labelText}>{t('vd.payPayTo')}</div>
                <select
                  value={billMethodId}
                  onChange={(e) => setBillMethodId(e.target.value)}
                  style={styles.input}
                >
                  {methods.map(m => (
                    <option key={m.id} value={String(m.id)}>
                      {m.bank_name || m.bank_code} — {m.account_number}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {/* Amount */}
            <label style={{ display: 'block', marginBottom: 10 }}>
              <div style={styles.labelText}>{t('vd.payAmountVnd')}</div>
              <input
                type="text"
                inputMode="numeric"
                value={billAmount}
                onChange={(e) => setBillAmount(e.target.value.replace(/[^\d]/g, ''))}
                placeholder="e.g. 185000"
                style={{ ...styles.input, fontSize: 18, fontWeight: 700, fontFamily: "'SF Mono', monospace" }}
              />
              {billAmountNum > 0 && (
                <div style={{ fontSize: 13, color: '#E46E34', fontWeight: 700, marginTop: 4 }}>
                  ₫ {billAmountNum.toLocaleString(dtLocale())}
                </div>
              )}
            </label>

            {/* Table number */}
            <label style={{ display: 'block', marginBottom: 10 }}>
              <div style={styles.labelText}>{t('vd.payTableOptional')}</div>
              <input
                type="text"
                value={billTable}
                onChange={(e) => setBillTable(e.target.value.slice(0, 10))}
                placeholder="e.g. 4"
                style={styles.input}
              />
            </label>

            {/* Memo */}
            <label style={{ display: 'block', marginBottom: 14 }}>
              <div style={styles.labelText}>{t('vd.payMemoOptional')}</div>
              <input
                type="text"
                value={billMemo}
                onChange={(e) => setBillMemo(e.target.value.slice(0, 50))}
                placeholder="e.g. Order #1023"
                style={styles.input}
                maxLength={50}
              />
            </label>

            {/* Generated URL preview */}
            {billUrl && (
              <div style={{
                background: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: 10,
                padding: 10, marginBottom: 12, wordBreak: 'break-all',
                fontSize: 11, fontFamily: "'SF Mono', monospace", color: '#1e40af',
              }}>
                {billUrl}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                style={{
                  ...styles.primaryBtn,
                  background: billAmountNum > 0 ? 'linear-gradient(135deg, #E46E34 0%, #d65a1f 100%)' : '#e5e7eb',
                  cursor: billAmountNum > 0 ? 'pointer' : 'default',
                  boxShadow: billAmountNum > 0 ? '0 4px 12px rgba(228,110,52,0.3)' : 'none',
                }}
                onClick={handleOpenBill}
                disabled={billAmountNum <= 0}
              >
                👁 {t('vd.payOpenBillPage')}
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  style={{ ...styles.outlineBtn, flex: 1, opacity: billAmountNum > 0 ? 1 : 0.5 }}
                  onClick={handleCopyBillUrl}
                  disabled={billAmountNum <= 0}
                >
                  📋 {t('vd.payCopyLink')}
                </button>
                <button
                  style={{ ...styles.outlineBtn, flex: 1, opacity: billAmountNum > 0 ? 1 : 0.5, background: '#25d366', color: 'white', borderColor: '#25d366' }}
                  onClick={handleShareWhatsApp}
                  disabled={billAmountNum <= 0}
                >
                  📱 WhatsApp
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Customer preview link */}
        <div style={{ ...styles.card, background: '#fffbeb', borderColor: '#fde68a', border: '1px solid #fde68a', marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
            <strong>{t('vd.payPreviewLabel')}</strong> {t('vd.payOpen')}{' '}
            <a href={`/pay/${slug}`} target="_blank" rel="noreferrer" style={{ color: '#92400e', fontWeight: 700 }}>
              /pay/{slug}
            </a>{' '}
            {t('vd.payToSeeWhatCustomersSee')}
          </div>
        </div>

        <div style={{ height: 40 }} />
      </div>
      <DashboardNavBar />
    </div>
  );
};

// ─── small components ───────────────────────────────────────────────────────

function Header({ title, onBack }) {
  const { t } = useTranslation();
  return (
    <div style={styles.header}>
      <button style={styles.backBtn} onClick={onBack} aria-label={t('vd.back')}>←</button>
      <span style={styles.headerTitle}>{title}</span>
      <div style={{ width: 40 }} />
    </div>
  );
}

// ─── styles ─────────────────────────────────────────────────────────────────

const styles = {
  screen: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    background: '#f9fafb',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    height: 64,
    background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 16px', flexShrink: 0, position: 'sticky', top: 0, zIndex: 100,
  },
  backBtn: {
    background: 'transparent', border: 'none', color: 'white', fontSize: 22,
    fontWeight: 700, cursor: 'pointer', padding: '4px 8px', borderRadius: 8,
    lineHeight: 1, width: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  headerTitle: {
    color: 'white', fontWeight: 700, fontSize: 16, flex: 1, textAlign: 'center',
    overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', padding: '0 8px',
  },
  body: {
    flex: 1, padding: '16px 16px 0', maxWidth: 560, width: '100%', margin: '0 auto', boxSizing: 'border-box',
  },
  venueNameStrip: { marginBottom: 12 },
  venueName: { fontSize: 14, color: '#9ca3af', fontWeight: 500 },
  card: {
    background: '#ffffff', borderRadius: 16, padding: 16,
    boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
  },
  labelText: {
    display: 'block', fontSize: 13, color: '#6b7280', fontWeight: 600, marginBottom: 6,
  },
  input: {
    display: 'block', width: '100%', boxSizing: 'border-box', padding: '11px 14px',
    fontSize: 15, color: '#111827', background: '#ffffff',
    border: '1.5px solid #e5e7eb', borderRadius: 10, outline: 'none',
    transition: 'border-color 0.15s ease', WebkitAppearance: 'none',
  },
  primaryBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: '100%', padding: '0 24px', height: 48,
    background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
    color: 'white', border: 'none', borderRadius: 12,
    fontSize: 15, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(59,130,246,0.3)', boxSizing: 'border-box',
  },
  outlineBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: '100%', height: 48, padding: '0 24px',
    background: 'white', color: '#374151', border: '1.5px solid #e5e7eb',
    borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer',
    boxSizing: 'border-box',
  },
  outlineBtnSmall: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '8px 14px', background: 'white', color: '#374151',
    border: '1.5px solid #e5e7eb', borderRadius: 8,
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  errorBanner: {
    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
    color: '#b91c1c', fontSize: 13, fontWeight: 500,
    padding: '10px 14px', marginBottom: 12, lineHeight: 1.4,
  },
  centreBlock: {
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    padding: 32, gap: 8, textAlign: 'center',
  },
  centreText: { fontSize: 14, color: '#9ca3af', margin: 0, marginTop: 8 },

  // ─── EV-1241 payment history ───
  histRefreshBtn: {
    width: 34, height: 34, flexShrink: 0,
    background: '#f3f4f6', color: '#374151', border: '1.5px solid #e5e7eb',
    borderRadius: 9, fontSize: 16, fontWeight: 700, cursor: 'pointer', lineHeight: 1,
  },
  histSortBtn: {
    flex: 1, padding: '9px 12px',
    background: 'white', color: '#374151', border: '1.5px solid #e5e7eb',
    borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
  histSortBtnActive: {
    background: '#ecfdf5', color: '#047857', borderColor: '#a7f3d0',
  },
  histRow: {
    border: '1px solid #f3f4f6', borderRadius: 12, overflow: 'hidden', background: '#fcfcfd',
  },
  histRowHead: {
    display: 'flex', alignItems: 'flex-start', padding: '11px 12px', cursor: 'pointer',
  },
  histTag: {
    fontSize: 10, fontWeight: 700, color: '#374151', background: '#f3f4f6',
    padding: '2px 7px', borderRadius: 7,
  },
  histProviderTag: {
    fontSize: 10, fontWeight: 700, color: '#3730a3', background: '#e0e7ff',
    padding: '2px 7px', borderRadius: 7, letterSpacing: 0.3,
  },
  histStatusTag: {
    fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 7, textTransform: 'uppercase',
  },
  histItems: {
    borderTop: '1px dashed #e5e7eb', background: '#fff',
    padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 5,
  },
  histItemRow: {
    display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
  },
};

export default VenueDashboardPaymentsPage;
